import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiCheck, HiOutlineTrash, HiX } from 'react-icons/hi';
import { useJwt } from '../../helpers/common';
import { useUserPreferencesService } from '../../services';
import { UserPreferenceItem } from "../../services/userPreferences";
import { removeUserPreference } from "../../redux/slices/organization";
import { toLocalTime } from '../../utils/timeUtils';
import ConfirmDeleteModal from '../Modals/ConfirmDeleteModal';

interface UserMemoryManageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_MEMORIES = 100;

const UserMemoryManageModal: React.FC<UserMemoryManageModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const jwtData: any = useJwt();
  const org_id = jwtData?.org_id || '';
  const { loading, fetchUserPreferences, deleteUserPreferences } = useUserPreferencesService();

  const {
    userPreferences = []
  } = useSelector((state: any) => state.organization);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [filterType, setFilterType] = useState<'all' | 'user' | 'reference'>('all');
  const [inlineConfirmId, setInlineConfirmId] = useState<number | null>(null);
  const [showConfirmAllModal, setShowConfirmAllModal] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen && org_id) {
      fetchUserPreferences(org_id, { limit: 100 });
      setSelectedIds([]);
      setInlineConfirmId(null);
      setIsSelectionMode(false);
    }
  }, [isOpen, org_id, fetchUserPreferences]);

  const items: UserPreferenceItem[] = userPreferences as UserPreferenceItem[];

  useEffect(() => {
    setSelectedIds([]);
    setInlineConfirmId(null);
  }, [filterType]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target as Node)) {
        setIsFilterMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredItems: UserPreferenceItem[] = items.filter((item: any) => {
    if (filterType === 'all') return true;
    const isReference = Boolean(item?.is_reference_chat_memory);
    return filterType === 'reference' ? isReference : !isReference;
  });
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every(it => selectedIds.includes(it.id));

  const handleToggleSelection = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredItems.some(it => it.id === id)));
    } else {
      const filteredIds = filteredItems.map(it => it.id);
      const union = Array.from(new Set([...selectedIds, ...filteredIds]));
      setSelectedIds(union);
    }
  };

  const handleDelete = () => {
    if (!org_id || selectedIds.length === 0) return;
    setShowConfirmAllModal(true);
  };

  const handleDeleteSingle = async (id: number) => {
    if (!org_id) return;

    try {
      await deleteUserPreferences(org_id, [String(id)]);
      dispatch(removeUserPreference(id));
      setSelectedIds(prev => prev.filter(sid => sid !== id));
      setInlineConfirmId(null);
    } catch (error) {
      console.error('Failed to delete user preference:', error);
    }
  };

  const handleConfirmDeleteAll = async () => {
    if (!org_id || selectedIds.length === 0) return;
    try {
      await deleteUserPreferences(org_id, selectedIds.map(id => String(id)));
      selectedIds.forEach(id => dispatch(removeUserPreference(id)));
      setSelectedIds([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error('Failed to delete user preferences:', error);
    } finally {
      setShowConfirmAllModal(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-4 mx-4 max-w-2xl w-full flex flex-col h-[550px]">
        <div className="flex items-center justify-between gap-2 pb-3 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg ml-1 ">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Saved user memory</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md transition-colors"
              aria-label="Close"
            >
              <HiX className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 pt-2">
          <div className="flex-1 overflow-auto space-y-2 pr-1 h-[400px]">
            {!loading && items.length > 0 && (
              <div className="sticky top-0 z-10 -mr-1 pr-1 pb-2 bg-gradient-to-b from-white/95 to-white/70 dark:from-slate-800/95 dark:to-slate-800/70 backdrop-blur border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="relative" ref={filterMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsFilterMenuOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={isFilterMenuOpen}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 hover:border-gray-300 dark:hover:border-slate-500 transition-all shadow-sm"
                    >
                      <span>{filterType === 'all' ? 'All memories' : filterType === 'user' ? 'User memory' : 'Reference chat memory'}</span>
                      <svg className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isFilterMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isFilterMenuOpen && (
                      <div
                        role="menu"
                        className="absolute left-0 top-full mt-1 w-48 rounded-lg border border-gray-100 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-xl p-1 z-20 animate-in fade-in zoom-in-95 duration-100"
                      >
                        <button
                          role="menuitem"
                          onClick={() => { setFilterType('all'); setIsFilterMenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${filterType === 'all' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600'}`}
                        >
                          All memories
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => { setFilterType('user'); setIsFilterMenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${filterType === 'user' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600'}`}
                        >
                          User memory
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => { setFilterType('reference'); setIsFilterMenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${filterType === 'reference' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600'}`}
                        >
                          Reference chat memory
                        </button>
                      </div>
                    )}
                  </div>

                  {isSelectionMode ? (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200">
                      <button
                        onClick={handleSelectAll}
                        className="text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 px-2 transition-colors"
                      >
                        {allFilteredSelected ? 'Deselect all' : 'Select all'}
                      </button>
                      <div className="h-4 w-px bg-gray-200 dark:bg-slate-600 mx-1"></div>
                      <button
                        onClick={() => { setIsSelectionMode(false); setSelectedIds([]); }}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-all shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={selectedIds.length === 0}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-all shadow-sm ${selectedIds.length === 0
                          ? 'bg-red-300 dark:bg-red-900/50 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 hover:shadow focus:ring-2 focus:ring-red-500/20 dark:bg-red-600 dark:hover:bg-red-700'
                          }`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsSelectionMode(true)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 hover:border-gray-300 dark:hover:border-slate-500 transition-all shadow-sm"
                    >
                      Select
                    </button>
                  )}
                </div>
              </div>
            )}
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, index) => (
                  <div key={index} className="group bg-gray-50 dark:bg-slate-700/50 rounded-lg p-1.5 border border-gray-200 dark:border-slate-600 animate-pulse">
                    <div className="flex items-center justify-between gap-2 h-8">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-1 px-2">
                          <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[120px]">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-full mb-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    No saved user memory yet
                  </h3>
                </div>
              </div>
            ) : (
              <div className={`space-y-2 transition-all duration-200 ${isSelectionMode ? 'pl-7' : ''}`}>
                {filteredItems.length === 0 ? (
                  <div className="flex items-center justify-center h-full min-h-[120px]">
                    <div className="text-center text-xs text-gray-500 dark:text-gray-400">No results for this filter.</div>
                  </div>
                ) : filteredItems.map(item => {
                  let content: string = '';
                  const raw = (item as any)?.value;
                  if (typeof raw === 'string') {
                    content = raw;
                  } else if (raw && typeof raw === 'object' && typeof raw.text === 'string') {
                    content = raw.text as string;
                  } else if (typeof (item as any)?.text === 'string') {
                    content = (item as any).text as string;
                  } else {
                    content = '';
                  }
                  const isReferenceChatMemory = Boolean((item as any)?.is_reference_chat_memory);
                  const isSelected = selectedIds.includes(item.id);

                  return (
                    <div key={item.id} className="group relative">
                      {isSelectionMode && (
                        <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleSelection(item.id);
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-0 dark:bg-gray-700 dark:border-gray-600"
                          />
                        </div>
                      )}
                      <div
                        onClick={() => {
                          if (isSelectionMode) {
                            handleToggleSelection(item.id);
                          }
                        }}
                        className={`group bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2 border flex-1 transition-all duration-200 ${isSelected
                          ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-slate-600'
                          } ${isSelectionMode ? 'cursor-pointer hover:border-blue-300 dark:hover:border-blue-500' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed line-clamp-2">
                              {content || '—'}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 whitespace-nowrap flex items-center gap-1">
                              <span>{isReferenceChatMemory ? 'Reference chat memory' : 'User memory'}</span>
                              <span className="text-gray-400 dark:text-gray-500">•</span>
                              <span>
                                {toLocalTime(item.created_at || '', { dateStyle: 'medium', timeStyle: 'short' })}
                              </span>
                            </div>
                          </div>
                          {!isSelectionMode && (
                            inlineConfirmId === item.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  aria-label="Confirm delete"
                                  className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/30 rounded text-green-600 dark:text-green-400 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteSingle(item.id); }}
                                >
                                  <HiCheck className="w-4 h-4" />
                                </button>
                                <button
                                  aria-label="Cancel delete"
                                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded text-gray-500 dark:text-gray-400 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); setInlineConfirmId(null); }}
                                >
                                  <HiX className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setInlineConfirmId(item.id); }}
                                  className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-all duration-200 text-red-600 dark:text-red-400 flex-shrink-0 opacity-80 hover:opacity-100"
                                  aria-label="Delete user memory"
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-between flex-col gap-1 pt-3 mt-2 border-t border-gray-200 dark:border-slate-700 text-center ">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Cadence keeps up to {MAX_MEMORIES} memories per user. Stored: {items.length}/{MAX_MEMORIES}.
            </div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500">
              Delete permanently removes the selected memory from your account.
            </div>
          </div>
        </div>
        <ConfirmDeleteModal
          showModal={showConfirmAllModal}
          setShowModal={setShowConfirmAllModal}
          handleConfirm={handleConfirmDeleteAll}
          title="Delete selected"
          description={`You are about to delete ${selectedIds.length} ${selectedIds.length === 1 ? 'memory' : 'memories'}. This action cannot be undone.`}
          confirmText="Delete"
        />
      </div>
    </div>
  );
};

export default UserMemoryManageModal;

